import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useTheme } from "../../context/ThemeContext";
import tinycolor from "tinycolor2";
import HeaderSection from "../../components/HeaderSection/HeaderSection";
import BottomSection from "../../components/BottomSection/BottomSection";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faPalette,
    faRandom,
    faBookmark,
    faCopy,
    faTrash,
} from "@fortawesome/free-solid-svg-icons";

const ColorPicker = () => {
    const [currentColor, setCurrentColor] = useState("#10b981");
    useTheme();
    const [savedColors, setSavedColors] = useState([]);

    const [toastMessage, setToastMessage] = useState("");
    const [showToast, setShowToast] = useState(false);

    // Color formats
    const [hex, setHex] = useState("#10b981");
    const [rgb, setRgb] = useState("rgb(16, 185, 129)");
    const [hsl, setHsl] = useState("hsl(160, 84%, 39%)");
    const [cmyk, setCmyk] = useState("cmyk(91%, 0%, 30%, 27%)");
    const [contrast, setContrast] = useState("Good (AA/AAA)");
    const [colorName, setColorName] = useState("Emerald");

    // Color conversion functions
    const hexToRgb = useCallback((hex) => {
        const bigint = parseInt(hex.slice(1), 16);
        const r = (bigint >> 16) & 255;
        const g = (bigint >> 8) & 255;
        const b = bigint & 255;
        return { r, g, b };
    }, []);

    const rgbToHsl = useCallback((r, g, b) => {
        r /= 255;
        g /= 255;
        b /= 255;
        const max = Math.max(r, g, b);
        const min = Math.min(r, g, b);
        let h,
            s,
            l = (max + min) / 2;

        if (max === min) {
            h = s = 0;
        } else {
            const d = max - min;
            s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
            switch (max) {
                case r:
                    h = (g - b) / d + (g < b ? 6 : 0);
                    break;
                case g:
                    h = (b - r) / d + 2;
                    break;
                case b:
                    h = (r - g) / d + 4;
                    break;
                default:
                    h = 0;
                    console.warn('Unexpected max value in HSL conversion.');
            }
            
            h /= 6;
        }

        return {
            h: Math.round(h * 360),
            s: Math.round(s * 100),
            l: Math.round(l * 100),
        };
    }, []);

    const rgbToCmyk = useCallback((r, g, b) => {
        if (r === 0 && g === 0 && b === 0) {
            return { c: 0, m: 0, y: 0, k: 100 };
        }

        let c = 1 - r / 255;
        let m = 1 - g / 255;
        let y = 1 - b / 255;
        const k = Math.min(c, m, y);

        c = (c - k) / (1 - k);
        m = (m - k) / (1 - k);
        y = (y - k) / (1 - k);

        return {
            c: Math.round(c * 100),
            m: Math.round(m * 100),
            y: Math.round(y * 100),
            k: Math.round(k * 100),
        };
    }, []);

    const getContrast = useCallback((hex) => {
        const { r, g, b } = hexToRgb(hex);
        const yiq = (r * 299 + g * 587 + b * 114) / 1000;
        return yiq >= 128 ? "Good (AA/AAA)" : "Low";
    }, [hexToRgb]);

    const getColorName = useCallback((hex) => {
        const names = {
            "#10b981": "Emerald",
            "#059669": "Green",
            "#047857": "Dark Green",
            "#065f46": "Deep Green",
            "#064e3b": "Forest",
            "#ecfdf5": "Mint",
            "#d1fae5": "Light Mint",
            "#a7f3d0": "Pale Green",
        };

        if (names[hex.toLowerCase()]) {
            return names[hex.toLowerCase()];
        }

        let closestColor = "";
        let minDistance = Infinity;

        for (const namedHex in names) {
            const distance = tinycolor.readability(hex, namedHex);
            if (distance < minDistance) {
                minDistance = distance;
                closestColor = names[namedHex];
            }
        }

        return closestColor || "Custom Color";
    }, []);

    const updateColor = useCallback((hex) => {
        const { r, g, b } = hexToRgb(hex);
        const { h, s, l } = rgbToHsl(r, g, b);
        const { c, m, y, k } = rgbToCmyk(r, g, b);

        setHex(hex);
        setRgb(`rgb(${r}, ${g}, ${b})`);
        setHsl(`hsl(${h}, ${s}%, ${l}%)`);
        setCmyk(`cmyk(${c}%, ${m}%, ${y}%, ${k}%)`);
        setContrast(getContrast(hex));
        setColorName(getColorName(hex));
    }, [hexToRgb, rgbToHsl, rgbToCmyk, getContrast, getColorName]);

    // Initialize
    useEffect(() => {
        const saved = JSON.parse(localStorage.getItem("savedColors")) || [];
        setSavedColors(saved);
        updateColor(currentColor);
    }, [currentColor, updateColor]);

    // Show toast message
    useEffect(() => {
        if (showToast) {
            const timer = setTimeout(() => {
                setShowToast(false);
            }, 2000);
            return () => clearTimeout(timer);
        }
    }, [showToast]);

    const showToastMessage = useCallback((message) => {
        setToastMessage(message);
        setShowToast(true);
    }, []);

    const generateShades = useCallback((baseHex) => {
        const shades = [];

        // Generate shades (darker)
        for (let i = 0; i <= 90; i += 10) {
            const shade = tinycolor(baseHex).darken(i).toHexString();
            shades.push(shade);
        }

        // Generate tints (lighter)
        for (let i = 10; i <= 90; i += 10) {
            const tint = tinycolor(baseHex).lighten(i).toHexString();
            shades.push(tint);
        }

        return shades;
    }, []);

    const handleColorChange = useCallback((e) => {
        const newColor = e.target.value;
        setCurrentColor(newColor);
        updateColor(newColor);
    }, [updateColor]);

    const getRandomColor = useCallback(() => {
        const letters = "0123456789ABCDEF";
        let color = "#";
        for (let i = 0; i < 6; i++) {
            color += letters[Math.floor(Math.random() * 16)];
        }
        setCurrentColor(color);
        updateColor(color);
    }, [updateColor]);

    const saveCurrentColor = useCallback(() => {
        if (!savedColors.includes(currentColor)) {
            const newSavedColors = [...savedColors, currentColor];
            setSavedColors(newSavedColors);
            localStorage.setItem("savedColors", JSON.stringify(newSavedColors));
            showToastMessage("Color saved!");
        } else {
            showToastMessage("Color already saved");
        }
    }, [currentColor, savedColors, showToastMessage]);

    const removeColor = useCallback((colorToRemove) => {
        const newSavedColors = savedColors.filter(
            (color) => color !== colorToRemove
        );
        setSavedColors(newSavedColors);
        localStorage.setItem("savedColors", JSON.stringify(newSavedColors));
        showToastMessage("Color removed");
    }, [savedColors, showToastMessage]);

    const clearSavedColors = useCallback(() => {
        setSavedColors([]);
        localStorage.removeItem("savedColors");
        showToastMessage("All colors cleared");
    }, [showToastMessage]);

    const copyToClipboard = useCallback((text) => {
        navigator.clipboard.writeText(text).then(() => {
            showToastMessage("Copied to clipboard!");
        });
    }, [showToastMessage]);

    const shades = useMemo(() => generateShades(currentColor), [currentColor, generateShades]);

    return (
        <>
            <HeaderSection toolId="colorPicker" />
            <div className="max-w-4xl mx-auto p-4 lg:max-w-full lg:px-4">
                <main className="bg-white rounded-lg dark:bg-gray-800 rounded-xl shadow-sm dark:shadow-gray-900/10 border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <div className="bg-gradient-to-r from-green-500 to-green-600 p-6 text-white">
                        <h1 className="text-2xl font-bold mb-2 flex items-center">
                            <FontAwesomeIcon icon={faPalette} className="mr-3" />
                            Color Picker Tool
                        </h1>
                        <p className="opacity-90">
                            Pick colors and get their properties
                        </p>
                    </div>
                    <article className="max-w-4xl mx-auto flex flex-col gap-6">
                        {/* Color Preview and Picker */}
                        <section className="p-6 bg-white dark:bg-gray-800 dark:border-gray-700">
                            <figure
                                className="w-full h-32 rounded-xl border border-green-200 mb-6 relative overflow-hidden"
                                style={{ backgroundColor: currentColor }}
                                aria-label={`Color preview: ${colorName}`}
                            >
                                <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-black/10 pointer-events-none"></div>
                            </figure>

                            <div className="flex gap-4 items-center">
                                <label className="flex-1">
                                    <span className="sr-only">Color picker</span>
                                    <input
                                        type="color"
                                        value={currentColor}
                                        onChange={handleColorChange}
                                        className="w-full h-12 cursor-pointer rounded-lg overflow-hidden border-2 border-green-200 hover:scale-[1.02] transition-transform focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                        aria-label="Select color"
                                    />
                                </label>
                                <button
                                    onClick={getRandomColor}
                                    className="px-4 py-2 bg-green-500 text-white rounded-lg flex items-center gap-2 hover:bg-green-600 hover:-translate-y-0.5 hover:shadow-md transition-all focus:ring-2 focus:ring-green-500 focus:outline-none"
                                    aria-label="Generate random color"
                                >
                                    <FontAwesomeIcon icon={faRandom} />
                                    Random
                                </button>
                            </div>
                        </section>

                        {/* Color Values */}
                        <section className="p-6 bg-white dark:bg-gray-800 dark:border-gray-700">
                            <h2 className="text-xl font-medium text-green-500 mb-4">
                                Color Values
                            </h2>
                            <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 dark:bg-gray-800 dark:border-gray-700 dark:text-white">
                                <li
                                    onClick={() => copyToClipboard(hex)}
                                    className="p-3 rounded-lg cursor-pointer relative transition-all hover:-translate-y-1 border border-green-200 hover:border-green-300 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100"
                                    tabIndex="0"
                                    aria-label={`HEX value: ${hex}. Click to copy`}
                                >
                                    <strong>HEX:</strong> <span>{hex}</span>
                                    <span className="absolute top-0 right-0 bg-green-500 text-white text-xs px-2 py-1 rounded-tr-lg rounded-bl-lg opacity-0 hover:opacity-100 transition-opacity">
                                        Copy
                                    </span>
                                </li>
                                <li
                                    onClick={() => copyToClipboard(rgb)}
                                    className="p-3 rounded-lg cursor-pointer relative transition-all hover:-translate-y-1 border border-green-200 hover:border-green-300 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100"
                                    tabIndex="0"
                                    aria-label={`RGB value: ${rgb}. Click to copy`}
                                >
                                    <strong>RGB:</strong> <span>{rgb}</span>
                                    <span className="absolute top-0 right-0 bg-green-500 text-white text-xs px-2 py-1 rounded-tr-lg rounded-bl-lg opacity-0 hover:opacity-100 transition-opacity">
                                        Copy
                                    </span>
                                </li>
                                <li
                                    onClick={() => copyToClipboard(hsl)}
                                    className="p-3 rounded-lg cursor-pointer relative transition-all hover:-translate-y-1 border border-green-200 hover:border-green-300 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100"
                                    tabIndex="0"
                                    aria-label={`HSL value: ${hsl}. Click to copy`}
                                >
                                    <strong>HSL:</strong> <span>{hsl}</span>
                                    <span className="absolute top-0 right-0 bg-green-500 text-white text-xs px-2 py-1 rounded-tr-lg rounded-bl-lg opacity-0 hover:opacity-100 transition-opacity">
                                        Copy
                                    </span>
                                </li>
                                <li
                                    onClick={() => copyToClipboard(cmyk)}
                                    className="p-3 rounded-lg cursor-pointer relative transition-all hover:-translate-y-1 border border-green-200 hover:border-green-300 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100"
                                    tabIndex="0"
                                    aria-label={`CMYK value: ${cmyk}. Click to copy`}
                                >
                                    <strong>CMYK:</strong> <span>{cmyk}</span>
                                    <span className="absolute top-0 right-0 bg-green-500 text-white text-xs px-2 py-1 rounded-tr-lg rounded-bl-lg opacity-0 hover:opacity-100 transition-opacity">
                                        Copy
                                    </span>
                                </li>
                            </ul>
                        </section>

                        {/* Color Information */}
                        <section className="rounded-xl border border-green-200 m-6 p-6 bg-white dark:bg-gray-800 dark:border-gray-700">
                            <h2 className="text-xl font-medium mb-4 text-green-500 dark:text-white">
                                Color Information
                            </h2>
                            <dl className="mb-6 dark:text-white">
                                <dt className="font-bold">Contrast Ratio:</dt>
                                <dd className="mb-2">{contrast}</dd>
                                <dt className="font-bold">Color Name:</dt>
                                <dd>{colorName}</dd>
                            </dl>
                            <nav className="flex flex-wrap gap-2">
                                <button
                                    onClick={saveCurrentColor}
                                    className="px-4 py-2 bg-green-500 text-white rounded-lg flex items-center gap-2 hover:bg-green-600 hover:-translate-y-0.5 hover:shadow-md transition-all focus:ring-2 focus:ring-green-500 focus:outline-none"
                                    aria-label="Save current color"
                                >
                                    <FontAwesomeIcon icon={faBookmark} />
                                    Save Color
                                </button>
                                <button
                                    onClick={() => copyToClipboard(hex)}
                                    className="px-4 py-2 border border-green-200 dark:text-white rounded-lg flex items-center gap-2 hover:-translate-y-0.5 hover:shadow-md transition-all hover:bg-green-100/20 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100"
                                    aria-label="Copy HEX value"
                                >
                                    <FontAwesomeIcon icon={faCopy} />
                                    Copy HEX
                                </button>
                                <button
                                    onClick={() => copyToClipboard(rgb)}
                                    className="px-4 py-2 border-green-200 dark:text-white rounded-lg flex items-center gap-2 hover:-translate-y-0.5 hover:shadow-md transition-all border border-green-200 hover:bg-green-100/20 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100"
                                    aria-label="Copy RGB value"
                                >
                                    <FontAwesomeIcon icon={faCopy} />
                                    Copy RGB
                                </button>
                            </nav>
                        </section>

                        {/* Saved Colors */}
                        <section className="rounded-xl border border-green-200 m-6 p-6 bg-white dark:bg-gray-800 dark:border-gray-700">
                            <header className="flex justify-between items-center mb-4">
                                <h2 className="text-xl font-medium text-green-500">
                                    Saved Colors
                                </h2>
                                <button
                                    onClick={clearSavedColors}
                                    className="px-4 py-2 border-green-200 dark:text-white rounded-lg flex items-center gap-2 hover:-translate-y-0.5 hover:shadow-md transition-all border border-green-200 hover:bg-green-100/20 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100"
                                    aria-label="Clear all saved colors"
                                >
                                    <FontAwesomeIcon icon={faTrash} />
                                    Clear
                                </button>
                            </header>
                            {savedColors.length > 0 ? (
                                <ul className="flex flex-wrap gap-3" aria-label="Saved colors">
                                    {savedColors.map((color, index) => (
                                        <li
                                            key={index}
                                            className="w-10 h-10 rounded-full cursor-pointer relative transition-all hover:scale-110 hover:shadow-md border border-green-200"
                                            style={{ backgroundColor: color }}
                                            onClick={() => {
                                                setCurrentColor(color);
                                                updateColor(color);
                                            }}
                                            onContextMenu={(e) => {
                                                e.preventDefault();
                                                removeColor(color);
                                            }}
                                            aria-label={`Saved color: ${color}. Click to select, right-click to remove`}
                                        >
                                            <span className="absolute -top-1 -right-1 bg-green-500 text-white text-xs w-4 h-4 rounded-full flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                                                ×
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <p className="text-gray-500">
                                    No colors saved yet
                                </p>
                            )}
                        </section>

                        {/* Shades & Tints */}
                        <section className="rounded-xl border border-green-200 m-6 p-6 bg-white dark:bg-gray-800 dark:border-gray-700">
                            <h2 className="text-xl font-medium mb-4 text-green-500">
                                Shades & Tints
                            </h2>
                            <ul className="flex flex-wrap gap-2" aria-label="Color shades and tints">
                                {shades.map((shade, index) => (
                                    <li
                                        key={index}
                                        className="w-10 h-10 rounded cursor-pointer transition-all hover:scale-110 hover:shadow-md relative border border-green-200"
                                        style={{ backgroundColor: shade }}
                                        onClick={() => {
                                            setCurrentColor(shade);
                                            updateColor(shade);
                                        }}
                                        aria-label={`Shade/tint: ${shade}`}
                                    >
                                        <span className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 text-xs opacity-0 hover:opacity-100 transition-opacity whitespace-nowrap">
                                            {shade}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </section>
                    </article>

                    {/* Toast Notification */}
                    {showToast && (
                        <aside
                            className="fixed bottom-8 left-1/2 transform -translate-x-1/2 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg transition-opacity opacity-100"
                            role="alert"
                            aria-live="assertive"
                        >
                            {toastMessage}
                        </aside>
                    )}
                </main>
            </div>
            <BottomSection toolId="colorPicker" />
        </>
    );
};

export default ColorPicker;